import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type {
  CloudflareAccessApplication,
  CloudflareAccessUser,
  CloudflareIdentityProvider,
  CloudflareServiceToken,
} from "../shared/types";
import {
  adminQueries,
  adminQueryKeys,
  optimisticallyUpdateCloudflareOverview,
  restoreCloudflareOverview,
  useAdminApi,
  useRefreshCloudflare,
  type CloudflareOptimisticChange,
} from "./admin-query";
import {
  CloudflareStatusBadge,
  MetricCard,
  PageHeader,
  PanelState,
  RefreshFailedNotice,
} from "./AdminUi";
import { errorMessage } from "./api";
import { CloudflareApplicationDialog } from "./CloudflareApplicationDialog";
import { CloudflareApplicationsSection } from "./CloudflareApplicationsSection";
import { CloudflareDevicesSection } from "./CloudflareDevicesSection";
import { CloudflareMcpSection } from "./CloudflareMcpSection";
import { CloudflarePostureSection } from "./CloudflarePostureSection";
import { CloudflareProvisioningSection } from "./CloudflareProvisioningSection";
import { CloudflareReusableControlsSection } from "./CloudflareReusableControlsSection";
import { CloudflareServiceTokensSection } from "./CloudflareServiceTokensSection";
import { CloudflareScimDialog } from "./CloudflareScimDialog";
import { CloudflareSessionsSection } from "./CloudflareSessionsSection";
import { ConfirmationDialog } from "./ConfirmationDialog";
import type { IconName } from "./Icon";
import { McpReadinessCard } from "./McpReadinessCard";
import { SecretDialog } from "./SecretDialog";
import { ScimSecretDialog } from "./ScimSecretDialog";
import { ServiceTokenDialog } from "./ServiceTokenDialog";

type PendingAction =
  | { kind: "application"; application: CloudflareAccessApplication }
  | { kind: "user"; user: CloudflareAccessUser }
  | {
      kind: "service-token";
      action: "toggle" | "refresh" | "delete";
      token: CloudflareServiceToken;
    };

type ServiceTokenRequest =
  | { kind: "create"; name: string; duration: string }
  | {
      kind: "rotate";
      token: CloudflareServiceToken;
      graceSeconds: number;
    };

function confirmation(action: PendingAction): {
  title: string;
  description: string;
  confirmLabel: string;
  destructive: boolean;
  icon: IconName;
} {
  if (action.kind === "application") {
    return {
      title: `Revoke every ${action.application.name} session?`,
      description:
        "All currently issued Access tokens for this application will stop working. Eligible users can sign in again.",
      confirmLabel: "Revoke application sessions",
      destructive: true,
      icon: "logout",
    };
  }
  if (action.kind === "user") {
    return {
      title: `Revoke ${action.user.email}?`,
      description:
        "This ends the user's Access sessions across applications. It does not disable the local directory account.",
      confirmLabel: "Revoke user sessions",
      destructive: true,
      icon: "logout",
    };
  }
  if (action.action === "refresh") {
    return {
      title: `Renew ${action.token.name}?`,
      description:
        "Cloudflare will extend this service token by one year. Its current secret remains unchanged.",
      confirmLabel: "Renew service token",
      destructive: false,
      icon: "rotate",
    };
  }
  if (action.action === "delete") {
    return {
      title: `Revoke ${action.token.name}?`,
      description:
        "This permanently deletes the service token. Every workload using it will immediately lose access.",
      confirmLabel: "Revoke service token",
      destructive: true,
      icon: "close",
    };
  }
  const enabling = !action.token.enabled;
  return {
    title: `${enabling ? "Enable" : "Disable"} ${action.token.name}?`,
    description: enabling
      ? "Cloudflare will accept this machine credential again for matching Service Auth policies."
      : "Cloudflare will immediately reject both the current and any overlapping previous secret.",
    confirmLabel: `${enabling ? "Enable" : "Disable"} service token`,
    destructive: !enabling,
    icon: enabling ? "check" : "close",
  };
}

function optimisticChange(
  action: PendingAction,
): CloudflareOptimisticChange | undefined {
  if (action.kind === "user") {
    return { type: "user-sessions-revoke", id: action.user.id };
  }
  if (action.kind !== "service-token") return undefined;
  if (action.action === "toggle") {
    return {
      type: "service-token-status",
      id: action.token.id,
      enabled: !action.token.enabled,
    };
  }
  if (action.action === "delete") {
    return { type: "service-token-delete", id: action.token.id };
  }
  return undefined;
}

export function CloudflarePanel() {
  const queryClient = useQueryClient();
  const adminApi = useAdminApi();
  const refresh = useRefreshCloudflare();
  const overview = useQuery(adminQueries.cloudflare());
  const [pending, setPending] = useState<PendingAction>();
  const [serviceTokenDialog, setServiceTokenDialog] = useState<
    CloudflareServiceToken | "create"
  >();
  const [managedApplication, setManagedApplication] =
    useState<CloudflareAccessApplication>();
  const [managedScimProvider, setManagedScimProvider] =
    useState<CloudflareIdentityProvider>();
  const [visibleSecret, setVisibleSecret] = useState<{
    clientId: string;
    clientSecret: string;
  }>();
  const [visibleScimSecret, setVisibleScimSecret] = useState<{
    endpoint: string;
    token: string;
  }>();

  const actionMutation = useMutation({
    mutationFn: async (action: PendingAction) => {
      if (action.kind === "application") {
        await adminApi(
          `/api/admin/cloudflare/applications/${encodeURIComponent(action.application.id)}/revoke-sessions`,
          {
            method: "POST",
            body: JSON.stringify({ confirmation: action.application.id }),
          },
        );
      } else if (action.kind === "user") {
        await adminApi(
          `/api/admin/cloudflare/users/${encodeURIComponent(action.user.id)}/revoke-sessions`,
          {
            method: "POST",
            body: JSON.stringify({
              email: action.user.email,
              confirmation: action.user.email,
            }),
          },
        );
      } else if (action.action === "toggle") {
        return adminApi<{ token: CloudflareServiceToken }>(
          `/api/admin/cloudflare/service-tokens/${encodeURIComponent(action.token.id)}/status`,
          {
            method: "PUT",
            body: JSON.stringify({
              name: action.token.name,
              enabled: !action.token.enabled,
              confirmation: action.token.id,
            }),
          },
        );
      } else if (action.action === "refresh") {
        return adminApi<{ token: CloudflareServiceToken }>(
          `/api/admin/cloudflare/service-tokens/${encodeURIComponent(action.token.id)}/refresh`,
          {
            method: "POST",
            body: JSON.stringify({ confirmation: action.token.id }),
          },
        );
      } else {
        await adminApi(
          `/api/admin/cloudflare/service-tokens/${encodeURIComponent(action.token.id)}`,
          {
            method: "DELETE",
            body: JSON.stringify({ confirmation: action.token.id }),
          },
        );
      }
      return undefined;
    },
    onMutate: async (action) => {
      const change = optimisticChange(action);
      if (!change) return {};
      await queryClient.cancelQueries({ queryKey: adminQueryKeys.cloudflare });
      return {
        snapshot: optimisticallyUpdateCloudflareOverview(queryClient, change),
      };
    },
    onError: (_caught, _action, context) => {
      if (context?.snapshot) {
        restoreCloudflareOverview(queryClient, context.snapshot);
      }
    },
    onSuccess: async (result) => {
      if (result?.token) {
        optimisticallyUpdateCloudflareOverview(queryClient, {
          type: "service-token-upsert",
          token: result.token,
        });
      }
      setPending(undefined);
      await refresh();
    },
  });

  const serviceTokenMutation = useMutation({
    mutationFn: async (request: ServiceTokenRequest) => {
      if (request.kind === "create") {
        const created = await adminApi<{
          token: CloudflareServiceToken;
          clientId: string;
          clientSecret: string;
        }>("/api/admin/cloudflare/service-tokens", {
          method: "POST",
          body: JSON.stringify({
            name: request.name,
            duration: request.duration,
          }),
        });
        // Show the new row immediately; the refresh below confirms it.
        optimisticallyUpdateCloudflareOverview(queryClient, {
          type: "service-token-upsert",
          token: created.token,
        });
        return {
          clientId: created.clientId,
          clientSecret: created.clientSecret,
        };
      }
      const rotated = await adminApi<{ clientSecret: string }>(
        `/api/admin/cloudflare/service-tokens/${encodeURIComponent(request.token.id)}/rotate`,
        {
          method: "POST",
          body: JSON.stringify({
            confirmation: request.token.id,
            graceSeconds: request.graceSeconds,
          }),
        },
      );
      return {
        clientId: request.token.clientId,
        clientSecret: rotated.clientSecret,
      };
    },
    onSuccess: async (secret) => {
      setServiceTokenDialog(undefined);
      setVisibleSecret(secret);
      await refresh();
    },
  });

  // Both dialogs report their own mutation error, so opening one must not
  // inherit the error left behind by the previous attempt.
  function openServiceTokenDialog(target: CloudflareServiceToken | "create") {
    serviceTokenMutation.reset();
    setServiceTokenDialog(target);
  }

  function confirmAction(action: PendingAction) {
    actionMutation.reset();
    setPending(action);
  }

  // Only a failure with nothing cached is fatal; a failed background refetch
  // keeps the panel — and any open one-time secret — on screen.
  if (!overview.data) {
    return <PanelState error={overview.isError} label="Cloudflare controls" />;
  }

  const {
    applications,
    automation,
    capabilities,
    cloudflare,
    findings,
    identityProviders,
    scimConnections,
    serviceTokens,
    sessions,
  } = overview.data;
  const policies = applications.flatMap((application) => application.policies);
  const activeSessions = sessions.reduce(
    (count, user) => count + user.sessions.length,
    0,
  );
  const criticalFindings = findings.filter(
    (finding) => finding.severity === "critical",
  ).length;
  const error =
    (actionMutation.error &&
      errorMessage(
        actionMutation.error,
        "The Cloudflare action could not be completed.",
      )) ||
    (serviceTokenMutation.error &&
      errorMessage(
        serviceTokenMutation.error,
        "The service credential could not be changed.",
      )) ||
    "";

  return (
    <>
      <PageHeader
        action={<CloudflareStatusBadge state={cloudflare.state} />}
        description="Access posture, sessions, identity synchronization, and machine credentials in one operational view."
        eyebrow="Cloudflare One"
        title="Cloudflare control plane"
      />

      {overview.isError && <RefreshFailedNotice />}

      <section
        className="stat-grid stat-grid--four"
        aria-label="Cloudflare summary"
      >
        <MetricCard
          detail={`${policies.length} attached policies`}
          label="Access applications"
          tone={applications.length ? "positive" : "neutral"}
          value={applications.length}
        />
        <MetricCard
          detail="Across Access users"
          label="Active sessions"
          value={activeSessions}
        />
        <MetricCard
          detail="Machine identities"
          label="Service tokens"
          value={serviceTokens.length}
        />
        <MetricCard
          detail="Require immediate review"
          label="Critical findings"
          tone={criticalFindings ? "warning" : "positive"}
          value={criticalFindings}
        />
      </section>

      {cloudflare.state === "not_configured" && (
        <div className="notice notice--warning page-notice">
          <strong>Cloudflare API integration is not configured.</strong>
          <span>
            Add these Worker bindings: {cloudflare.missingBindings.join(", ")}.
          </span>
        </div>
      )}
      {cloudflare.state === "degraded" && (
        <div className="notice notice--warning page-notice" role="alert">
          <strong>Some Cloudflare capabilities are unavailable.</strong>
          <span>{cloudflare.message}</span>
        </div>
      )}
      {error && (
        <div className="notice notice--error page-notice" role="alert">
          {error}
        </div>
      )}

      <CloudflarePostureSection
        findings={findings}
        identityCapability={capabilities.identity}
        identityProviders={identityProviders}
        onManageScim={setManagedScimProvider}
        scimConnections={scimConnections}
      />
      <CloudflareApplicationsSection
        applications={applications}
        capability={capabilities.applications}
        onManage={setManagedApplication}
        onRevoke={(application) =>
          confirmAction({ kind: "application", application })
        }
      />
      {managedApplication && (
        <CloudflareApplicationDialog
          application={managedApplication}
          onClose={() => setManagedApplication(undefined)}
        />
      )}
      {managedScimProvider && (
        <CloudflareScimDialog
          connection={scimConnections.find(
            (connection) =>
              connection.identityProviderId === managedScimProvider.id,
          )}
          identityProvider={
            identityProviders.find(
              (provider) => provider.id === managedScimProvider.id,
            ) ?? managedScimProvider
          }
          onClose={() => setManagedScimProvider(undefined)}
          onReveal={setVisibleScimSecret}
        />
      )}
      <CloudflareSessionsSection
        activeSessions={activeSessions}
        capability={capabilities.sessions}
        onRevoke={(user) => confirmAction({ kind: "user", user })}
        users={sessions}
      />
      <CloudflareDevicesSection />
      <div className="cloudflare-summary-grid">
        <CloudflareServiceTokensSection
          capability={capabilities.serviceTokens}
          onCreateToken={() => openServiceTokenDialog("create")}
          onDeleteToken={(token) =>
            confirmAction({ kind: "service-token", action: "delete", token })
          }
          onRefreshToken={(token) =>
            confirmAction({ kind: "service-token", action: "refresh", token })
          }
          onRotateToken={openServiceTokenDialog}
          onToggleToken={(token) =>
            confirmAction({ kind: "service-token", action: "toggle", token })
          }
          serviceTokens={serviceTokens}
        />
        <McpReadinessCard applications={applications} />
      </div>
      <CloudflareMcpSection
        applications={applications}
        serviceTokens={serviceTokens}
      />
      <CloudflareReusableControlsSection applications={applications} />
      <CloudflareProvisioningSection
        automation={automation}
        identityProviders={identityProviders}
        registryCapability={capabilities.sessions}
        registryUsers={sessions}
        scimConnections={scimConnections}
      />

      {serviceTokenDialog && (
        <ServiceTokenDialog
          busy={serviceTokenMutation.isPending}
          error={
            serviceTokenMutation.error
              ? errorMessage(
                  serviceTokenMutation.error,
                  "The service credential could not be changed.",
                )
              : ""
          }
          onClose={() => setServiceTokenDialog(undefined)}
          onCreate={(name, duration) =>
            serviceTokenMutation.mutate({ kind: "create", name, duration })
          }
          onRotate={(token, graceSeconds) =>
            serviceTokenMutation.mutate({ kind: "rotate", token, graceSeconds })
          }
          token={
            serviceTokenDialog === "create" ? undefined : serviceTokenDialog
          }
        />
      )}

      {visibleSecret && (
        <SecretDialog
          clientId={visibleSecret.clientId}
          clientSecret={visibleSecret.clientSecret}
          mode="issued"
          onClear={() => setVisibleSecret(undefined)}
        />
      )}

      {visibleScimSecret && (
        <ScimSecretDialog
          endpoint={visibleScimSecret.endpoint}
          onClear={() => setVisibleScimSecret(undefined)}
          token={visibleScimSecret.token}
        />
      )}

      {pending && (
        <ConfirmationDialog
          busy={actionMutation.isPending}
          error={
            actionMutation.error
              ? errorMessage(
                  actionMutation.error,
                  "The Cloudflare action could not be completed.",
                )
              : ""
          }
          onCancel={() => !actionMutation.isPending && setPending(undefined)}
          onConfirm={() => actionMutation.mutate(pending)}
          {...confirmation(pending)}
        />
      )}
    </>
  );
}
